import { cva, type VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';
import { OverlayArrow, Tooltip, TooltipTrigger, TooltipTriggerComponentProps } from 'react-aria-components';
import { cn } from './utils';

const agTooltipVariants = cva(
  'z-50 bg-neutral-12 text-white text-12 px-2 py-1 rounded shadow-md max-w-[250px] entering:animate-in entering:fade-in-0 entering:zoom-in-95 exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95',
  {
    variants: { placement: { top: 'mb-2', bottom: 'mt-2', left: 'mr-2', right: 'ml-2' } },
    defaultVariants: { placement: 'top' },
  }
);

type AgTooltipVariantProps = VariantProps<typeof agTooltipVariants>;

export interface AgTooltipProps extends Omit<TooltipTriggerComponentProps, 'children'> {
  /** The content to show in the tooltip */
  content: ReactNode;
  /** The element that triggers the tooltip */
  children: ReactNode;
  /** Placement of the tooltip relative to the trigger */
  placement?: AgTooltipVariantProps['placement'];
  /** Additional class for the tooltip */
  className?: string;
  /** Show arrow pointing to trigger */
  showArrow?: boolean;
}

export function AgTooltip({
  content,
  children,
  placement = 'top',
  className,
  showArrow = true,
  delay = 500,
  closeDelay = 0,
  ...props
}: AgTooltipProps) {
  if (!content) {
    return <>{children}</>;
  }

  return (
    <TooltipTrigger delay={delay} closeDelay={closeDelay} {...props}>
      {children}
      <Tooltip placement={placement} className={cn(agTooltipVariants({ placement }), className)}>
        {showArrow && (
          <OverlayArrow className="group">
            <svg
              width={8}
              height={8}
              viewBox="0 0 8 8"
              className="fill-neutral-12 group-data-[placement=bottom]:rotate-180 group-data-[placement=left]:-rotate-90 group-data-[placement=right]:rotate-90"
            >
              <path d="M0 0 L4 4 L8 0" />
            </svg>
          </OverlayArrow>
        )}
        {content}
      </Tooltip>
    </TooltipTrigger>
  );
}

AgTooltip.displayName = 'AgTooltip';
